import type { NavigateFunction } from 'react-router-dom';
import type { AcaoInterfaceMia } from './assistenteService';

/**
 * O que a Mia faz na tela, sempre pelo roteador e pelos atributos da propria pagina, nunca
 * por script vindo do modelo (plano da Mia, fase 3).
 */
const CLASSE_DESTAQUE = 'mia-destaque';
const MAX_ALVOS = 40;

/** Elementos marcados com data-alvo, visiveis e fora do painel da Mia. */
export function coletarAlvos(raiz: ParentNode = document): { id: string; rotulo?: string }[] {
  const vistos = new Set<string>();
  const alvos: { id: string; rotulo?: string }[] = [];
  for (const el of raiz.querySelectorAll<HTMLElement>('[data-alvo]')) {
    const id = el.dataset.alvo ?? '';
    if (!/^[a-z0-9-]{1,40}$/.test(id) || vistos.has(id)) continue;
    if (el.closest('[data-painel-mia]')) continue;
    if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) continue;
    vistos.add(id);
    const rotulo = (el.dataset.alvoRotulo ?? el.getAttribute('aria-label') ?? el.textContent ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 60);
    alvos.push(rotulo ? { id, rotulo } : { id });
    if (alvos.length >= MAX_ALVOS) break;
  }
  return alvos;
}

/** Rola ate o elemento e o realca por alguns segundos. false se ele nao esta na tela. */
export function destacarNaTela(alvo: string, duracaoMs = 3500): boolean {
  if (!/^[a-z0-9-]{1,40}$/.test(alvo)) return false;
  const el = document.querySelector<HTMLElement>(`[data-alvo="${alvo}"]`);
  if (!el || el.closest('[data-painel-mia]')) return false;
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.classList.remove(CLASSE_DESTAQUE);
  // Reinicia a animacao quando o mesmo elemento e destacado de novo.
  void el.offsetWidth;
  el.classList.add(CLASSE_DESTAQUE);
  window.setTimeout(() => el.classList.remove(CLASSE_DESTAQUE), duracaoMs);
  return true;
}

/** Mesma largura do CSS responsivo: ate 768px o painel da Mia cobre a tela. */
export function ehCelular(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Destaca o elemento. No celular o painel cobre a tela: se o elemento existe, recolhe o painel
 * antes e destaca depois que ele sai (a pilula "Voltar para a Mia" reabre a conversa).
 */
export function mostrarNaTela(alvo: string, recolher?: () => void) {
  const el = /^[a-z0-9-]{1,40}$/.test(alvo) ? document.querySelector(`[data-alvo="${alvo}"]`) : null;
  if (recolher && el && !el.closest('[data-painel-mia]') && ehCelular()) {
    recolher();
    window.setTimeout(() => destacarNaTela(alvo), 250);
    return;
  }
  // Depois da pintura, para o elemento ja estar na tela.
  window.requestAnimationFrame(() => destacarNaTela(alvo));
}

/** Abre as Atividades do aluno com o pedido de registro no state (a tela abre o modal). */
export function abrirRegistro(acao: Extract<AcaoInterfaceMia, { tipo: 'abrir_registro' }>, navigate: NavigateFunction) {
  navigate(acao.path, { state: { registroPedido: { ...acao.registro, chave: Date.now() } } });
}

/** Acoes que acontecem sozinhas quando a resposta chega. Trocar escola espera o Confirmar. */
export function executarAcaoInterface(acao: AcaoInterfaceMia, navigate: NavigateFunction, recolher?: () => void) {
  if (acao.tipo === 'navegar') {
    navigate(acao.path);
  } else if (acao.tipo === 'abrir_registro') {
    abrirRegistro(acao, navigate);
  } else if (acao.tipo === 'destacar') {
    mostrarNaTela(acao.alvo, recolher);
  }
}
